import { useLink } from '@tbitop/routing';
import classNames from 'classnames';
import type { ComponentProps, PropsWithChildren, ReactNode } from 'react';
import { forwardRef } from 'react';
import { Tooltip } from '../../tooltip';
import type { IconComponent } from '../../types';
import { ApplicationHeader } from '../header/header';
import { Subheader } from '../typography/typography';

type App = {
  name: string;
  iconUrl: string;
  href: string;
};

type ShellPortalProps = {
  currentApp?: App;
  user: ComponentProps<typeof ApplicationHeader>['user'];
  onNavigationProfileClick: ComponentProps<typeof ApplicationHeader>['onNavigationProfileClick'];
  className?: string;
  navigation: ReactNode;
};

export const ShellPortal = ({ currentApp, user, className, onNavigationProfileClick, children, navigation }: PropsWithChildren<ShellPortalProps>) => (
  <>
    <ApplicationHeader className={className} onNavigationProfileClick={onNavigationProfileClick} user={user}>
      <PlatformLogo />
      {currentApp ? <AppLogo app={currentApp} /> : null}
      <nav aria-label="Portal-Navigation" className="flex flex-row justify-end flex-1 gap-200">
        {navigation}
      </nav>
    </ApplicationHeader>
    {children}
  </>
);

const PlatformLogo = () => {
  const Link = useLink();
  return (
    <Subheader as="div" className="flex flex-row items-center fill-tds-color-interactive-primary-20 gap-50">
      <Link href="/portal">
        <img className="inline-block w-500 h-500" src="/assets/images/one-platform.svg" alt="" aria-hidden />
      Team Beverage Portal
      </Link>
    </Subheader>
  );
};

type AppLogoProps = {
  app: App;
};

const AppLogo = ({ app }: AppLogoProps) => {
  const Link = useLink();
  return (
    <Link href={app.href} className="flex flex-row items-center border-l tds-app-name gap-200 pl-200 border-tds-color-interactive-primary-70">
      <img className="inline-block w-500 h-500" src={app.iconUrl} alt="" aria-hidden />
      {app.name}
    </Link>
  );
};

type NavigationItemProps = {
  label: string;
  isActive?: boolean;
  className?: string;
  Icon: IconComponent;
};

export const ShellPortalNavigationItem = ({ label, Icon, className, isActive }: PropsWithChildren<NavigationItemProps>) => (
  <ShellPortalNavigationItem.Root>
    <ShellPortalNavigationItem.Content label={label} className={className} isActive={isActive} Icon={Icon} />
  </ShellPortalNavigationItem.Root>
);

ShellPortalNavigationItem.Root = ({ children }: PropsWithChildren<unknown>) => (
  <span className="border-r last:border-r-0 pr-200 last:pr-0 border-tds-color-interactive-primary-70">
    {children}
  </span>
);

ShellPortalNavigationItem.Content = forwardRef<HTMLSpanElement, NavigationItemProps>(({ label, isActive, className, Icon }, ref) => (
  <Tooltip content={label} side="bottom">
    <span ref={ref} className={classNames(
      'tds-navigation-item p-100 inline-flex box-content',
      { 'active': isActive },
      className,
    )} >
      <Icon weight="regular" />
      <span className="sr-only">{label}</span>
    </span>
  </Tooltip>
));
